import React, { useRef, useEffect } from "react"
import Button from "@src/components/core-button"
import Spinner from "@src/components/core-spinner"
import { UserContext } from "@src/context/user-context"
import FormInput from "../../reusable/forms/form-input"
import { useForm, FormProvider } from "react-hook-form"
import {
  validateEmail,
  isAtLeast8Chars,
  hasNumber,
  hasSymbol,
  hasUpperCase,
  hasLowerCase,
} from "@src/utils/validators"
import { FormActions } from "../../reusable/forms/auth-forms.styled"

function FormSignup({ status, setStatus, switchToVerify, setEmail, children }) {
  const methods = useForm()
  const { watch } = methods
  const email = watch("email")
  const password = watch("password")
  const formRef = useRef()

  useEffect(() => {
    setEmail(email)
  }, [email, setEmail])

  const handleProgrammaticSubmit = () => {
    if (formRef.current) {
      formRef.current.dispatchEvent(
        new Event("submit", { cancelable: true, bubbles: true }),
      )
    }
  }

  const validateConfirmPassword = value => {
    if (value !== password) {
      return "Passwords must match"
    }
    return true
  }

  const passwordValidators = value => {
    let error = false
    let string = ""

    const messages = [
      "Must be at least 8 characters",
      "Must include a number",
      "Must include a symbol (!@#$%^&*()-_+=)",
      "Must include an uppercase letter",
      "Must include a lowercase letter",
    ]
    let index = 0

    for (let validation of [
      isAtLeast8Chars,
      hasNumber,
      hasSymbol,
      hasUpperCase,
      hasLowerCase,
    ]) {
      const result = validation(value)

      if (result !== true) {
        error = true
        string += `<span> <div class="icon-close" ></div> ${messages[index]}</span> `
      } else {
        string += `<span> <div class="icon-check" ></div> ${messages[index]}</span> `
      }

      index++
    }

    return error ? string : true
  }

  return (
    <FormProvider {...methods}>
      <p className="h2">
        Create an account{" "}
        <img
          src="/images/user-flow-sign-up-diamond.svg"
          alt="sign-up-diamond"
          width={18}
          height={23}
        />
      </p>
      <p className="center">
        {status.success
          ? `A verification code has been sent to ${
              email ? email : "your email"
            }.`
          : "Start your adventure today."}
      </p>
      <UserContext.Consumer>
        {({ signup }) => {
          const handleSubmit = ({ email, password }) => {
            setStatus({ ...setStatus, pending: true })
            signup(email, password)
              .then(() => {
                setStatus({
                  success: true,
                  pending: false,
                  formMessage: null,
                })
              })
              .catch(error => {
                setStatus({
                  success: false,
                  pending: false,
                  formMessage: error,
                })
              })
          }
          return (
            <>
              {status.success ? null : (
                <form
                  ref={formRef}
                  onSubmit={methods.handleSubmit(handleSubmit)}
                >
                  <FormInput
                    label="Email"
                    name="email"
                    type="email"
                    validate={validateEmail()}
                  />
                  <FormInput
                    label="Password"
                    name="password"
                    type="password"
                    validate={passwordValidators}
                  />
                  <FormInput
                    label="Confirm Password"
                    name="confirmPassword"
                    type="password"
                    validate={validateConfirmPassword}
                  />
                </form>
              )}
              <FormActions>
                {status.formMessage && (
                  <p className="form-error">{status.formMessage}</p>
                )}
                {status.success ? (
                  <Button variant="rounded" onClick={switchToVerify}>
                    Verify Account
                  </Button>
                ) : (
                  <Button
                    variant="rounded"
                    type="submit"
                    disabled={status.pending}
                    onClick={handleProgrammaticSubmit}
                  >
                    Sign Up{" "}
                    {status.pending && <Spinner color="white" size="sm" />}
                  </Button>
                )}
                {children}
              </FormActions>
            </>
          )
        }}
      </UserContext.Consumer>
    </FormProvider>
  )
}

export default FormSignup
